'use client';

import { Plan } from '@/app/(intakeFormV2)/intake-v2/types';
import { ActionButton } from '@/components/ui/ActionButton';
import { Text } from '@/components/ui/Text';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import Image from 'next/image';
import { ReactNode, useCallback, useState } from 'react';

export const PLANS: Plan[] = ['Signature', 'Platinum', 'Pediatrics'];

const Pricing: Record<Plan, string> = {
  Signature: '+$4,000',
  Platinum: '+$8,000',
  Pediatrics: '+$2,000',
};

const Backgrounds: Record<Plan, string> = {
  Pediatrics: 'bg-sh-paper',
  Platinum: 'bg-sh-cream',
  Signature: 'bg-sh-teal',
};

const PlanImage: Record<Plan, string> = {
  Signature: '/sol-signature-img.png',
  Platinum: '/sol-platinum-img.png',
  Pediatrics: '/sol-pediatrics-img.png',
};

const PlanDescription: Record<Plan, string> = {
  Signature: '',
  Platinum:
    'Our most comprehensive membership tier removes limits and expands access for unmatched peace of mind.',
  Pediatrics:
    'Complete concierge care for your child. With comprehensive primary, urgent, and emergency pediatrics—only available in Southern California.',
};

export const PlanDetails: Record<Plan, string[]> = {
  Signature: [
    'Unlimited visits to all Sollis Health centers',
    '24/7 access to world-class ER doctors and clinicians',
    'Always-on virtual care, accessible from around the world',
    'Immediate in-house diagnostics and urgent procedures',
    'VIP referrals to world-class specialists, without the delays',
    'Welcome Consultation to establish care and assess your baseline health',
    "Personalized aftercare that ensures you're never on your own",
    'Access to premium services like house calls and advanced imaging, available for an additional fee',
  ],
  Platinum: [
    'All the benefits of our Signature Membership',
    'High-touch oversight with medical leadership and enhanced member support',
    'All-inclusive advanced imaging (X-ray, EKG, ultrasound, CT, MRI, cardiovascular)*',
    '3 complimentary house calls per member, each year',
    'Choose between a complimentary: Galleri cancer screening, Wyndly environmental allergy test, or heart CT calcium scan**',
    'Clinically indicated vitamin infusions, saline hydration, and other premium member services',
  ],
  Pediatrics: [
    'On-site Concierge Pediatrician',
    '24/7 urgent and emergency care from ER-trained doctors',
    '24/7 virtual care and global travel support',
    'Unlimited butterfly ultrasounds and EKGs (as medically necessary)',
    'Expedited specialist referrals',
    'Care navigation and hospital advocacy',
    'Care coordination with your child’s other doctors',
    'Camp and school physicals',
    'Complimentary allergy testing (food and environmental)',
    '1 complimentary house call',
  ],
};

const PlanComparison: Record<
  string,
  Record<Plan, { checked: boolean; text?: string }>
> = {
  'Unlimited visits to all Sollis Health centers': {
    Signature: {
      checked: true,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: true,
    },
  },
  '24/7 access to world-class ER doctors and clinicians': {
    Signature: {
      checked: true,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: true,
    },
  },
  'Always-on virtual care, accessible from around the world': {
    Signature: {
      checked: true,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: true,
    },
  },
  'Immediate in-house diagnostics and urgent procedures': {
    Signature: {
      checked: true,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: true,
    },
  },
  '3x-faster referrals to world-class specialists': {
    Signature: {
      checked: true,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: true,
    },
  },
  'Welcome Consultation to establish care and assess your baseline health': {
    Signature: {
      checked: true,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: true,
    },
  },
  "Personalized aftercare that ensures you're never on your own": {
    Signature: {
      checked: true,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: true,
    },
  },
  'All-inclusive advanced imaging (CT, X-ray, MRI, ultrasound)': {
    Signature: {
      checked: false,
    },
    Platinum: {
      checked: true,
    },
    Pediatrics: {
      checked: false,
    },
  },
  'House calls per year': {
    Signature: {
      checked: false,
      text: 'Available for an additional fee',
    },
    Platinum: {
      checked: false,
      text: '3 complimentary',
    },
    Pediatrics: {
      checked: false,
      text: '1 complimentary',
    },
  },
  'Choice of one complimentary service or procedure: Galleri cancer screening, Wyndly allergy test, or heart CT calcium scan':
    {
      Signature: {
        checked: false,
      },
      Platinum: {
        checked: true,
      },
      Pediatrics: {
        checked: false,
      },
    },
  'Medically indicated vitamin infusions, saline hydration, and other premium medical services':
    {
      Signature: {
        checked: false,
      },
      Platinum: {
        checked: true,
      },
      Pediatrics: {
        checked: false,
      },
    },
  'All-in-one primary, urgent, and emergency pediatrics with on-site Concierge Pediatrician':
    {
      Signature: {
        checked: false,
      },
      Platinum: {
        checked: false,
      },
      Pediatrics: {
        checked: true,
      },
    },
  'Well visits, vaccines, complimentary allergy testing, camp and school physicals':
    {
      Signature: {
        checked: false,
      },
      Platinum: {
        checked: false,
      },
      Pediatrics: {
        checked: true,
      },
    },
};

const Checkmark = () => (
  <div className='my-auto inline-block rounded-full bg-[#B0AEAC] p-1'>
    <Check color='white' height='16px' width='16px' />
  </div>
);

const CheckedListItem = (props: {
  children: string | ReactNode;
  color: string;
}) => {
  const style = `my-auto shrink-0 rounded-full ${props.color} p-1`;

  return (
    <div className='flex gap-[16px]'>
      <div className={style}>
        <Check color='white' height='16px' width='16px' />
      </div>
      <div className='min-w-0 flex-1 wrap-break-word'>{props.children}</div>
    </div>
  );
};

export const MobilePlanUnlocksList = ({
  plan,
  expanded: expandedProp,
  onToggle,
}: {
  plan: Plan;
  expanded?: boolean;
  onToggle?: (next: boolean) => void;
}) => {
  const details = PlanDetails[plan];
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const handleToggle = () => {
    const next = !expanded;
    if (onToggle) {
      onToggle(next);
    }
    if (!isControlled) {
      setInternalExpanded(next);
    }
  };

  return (
    <>
      <div className='flex items-center justify-center self-stretch pl-4'>
        <button
          type='button'
          onClick={handleToggle}
          className='sh-body cursor-pointer text-[18px] text-black underline'
        >
          <div className='flex items-center'>
            <div>Learn more about Sollis {plan} </div>
            <div>{expanded ? <ChevronUp /> : <ChevronDown />}</div>
          </div>
        </button>
      </div>
      {expanded && (
        <div className='my-4 px-[16px]'>
          <Text variant='body' bold>
            {plan} Membership includes:
          </Text>
          <ul>
            {details.map((u, i) => (
              <li className='my-4' key={i}>
                <CheckedListItem color='bg-[#B0AEAC]'>
                  <Text variant='body' color='dark'>
                    {u}
                  </Text>
                </CheckedListItem>
              </li>
            ))}
          </ul>
          {details.some(u => u.includes('*')) && (
            <span className='text-sm'>
              *When clinically indicated. Availability depending on location.
            </span>
          )}
        </div>
      )}
    </>
  );
};

export const PlanUnlocksList = ({ plan }: { plan: Plan }) => {
  const details = PlanDetails[plan];

  return (
    <div className='px-8'>
      <Text variant='body' bold>
        Your Sollis {plan} Membership includes:
      </Text>
      <ul>
        {details.map((u, i) => (
          <li className='my-4' key={i}>
            <CheckedListItem color='bg-[#B0AEAC]'>
              <Text variant='body' color='dark'>
                {u}
              </Text>
            </CheckedListItem>
          </li>
        ))}
      </ul>
      {details.some(u => u.includes('*')) && (
        <>
          <div className='text-sm'>
            *When clinically indicated. Availability depending on location.
          </div>
          <div className='text-sm'>
            **CT scan is for members over 40. Availability depending on
            location.
          </div>
        </>
      )}
    </div>
  );
};

export const MarketingCard = ({
  plan,
  callback,
}: {
  plan: Plan;
  callback: (plan: Plan) => void;
}) => {
  const details = PlanDetails[plan];
  const cost = Pricing[plan];
  const description = PlanDescription[plan];
  const label = `Upgrade To ${plan}`;
  const src = PlanImage[plan];

  const handleEditQuote = useCallback(() => {
    callback(plan);
  }, [callback, plan]);

  return (
    <div className='bg-sh-cream flex flex-col gap-[50px] rounded-4xl p-8 md:flex-row'>
      <div className='flex flex-1 flex-grow flex-col gap-[24px]'>
        <h3 className='text-[32px]'>Sollis {plan} Membership starts at</h3>
        <p className='text-[32px] font-bold'>{cost} / year</p>
        <p className='text-[18px]'>{description}</p>
        <div className='w-full md:w-auto'>
          <ActionButton
            label={label}
            onClick={handleEditQuote}
            variant='primary'
            className='w-full md:w-auto'
          />
        </div>
      </div>
      <div className='sh-body flex-1 flex-grow text-[18px]'>
        <p>What&apos;s included:</p>
        <ul>
          {details.map((u, i) => (
            <li className='my-4' key={i}>
              <CheckedListItem color='bg-[#292722]'>{u}</CheckedListItem>
            </li>
          ))}
        </ul>
        {details.some(u => u.includes('*')) && (
          <>
            <div className='text-sm text-[18px]'>
              *When clinically indicated. Availability depending on location.
            </div>
            <div className='text-sm text-[18px]'>
              **CT scan is for members over 40. Availability depending on
              location.
            </div>
          </>
        )}
      </div>
      <div className='flex-1 flex-grow'>
        <Image
          src={src}
          alt={`${plan} image`}
          height={537}
          width={400}
          className='w-full rounded-xl'
        />
      </div>
    </div>
  );
};

export type PlanTableProps = {
  showPediatrics?: boolean;
};

export const PlanTable = ({ showPediatrics = true }: PlanTableProps) => {
  const visiblePlans = PLANS.filter(
    plan => showPediatrics || plan !== 'Pediatrics'
  );
  const planColWidth = `${(50 / visiblePlans.length).toFixed(2)}%`;

  return (
    <div className='rounded-4xl bg-white p-[60px]'>
      <div className='pb-[32px] text-center'>
        <h3 className='font-serif text-[60px] leading-[110%] font-light'>
          Compare Sollis Memberships
        </h3>
      </div>
      <table>
        <colgroup>
          <col style={{ width: '50%' }} />
          {visiblePlans.map(plan => (
            <col key={plan} style={{ width: planColWidth }} />
          ))}
        </colgroup>
        <thead>
          <tr className='text-[20px]'>
            <th className='border-b-2 border-[#292722] p-[16px] text-left'>
              Features
            </th>
            {visiblePlans.map(plan => (
              <th
                key={plan}
                className={`${Backgrounds[plan]} rounded-t-2xl border-b-2 border-[#292722] p-[16px] text-center`}
              >
                {plan}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(PlanComparison)
            .filter(([, details]) =>
              visiblePlans.some(
                plan => details[plan].checked || details[plan].text
              )
            )
            .map(([msg, details]) => (
              <tr className='font-sans text-[16px]' key={msg}>
                <td className='border-1 border-[#BBBBBB] px-[16px] py-[17px]'>
                  {msg}
                </td>
                {visiblePlans.map(plan => (
                  <td
                    key={plan}
                    className={`border-1 border-[#BBBBBB] px-[16px] py-[8px] text-center ${Backgrounds[plan]}`}
                  >
                    {details[plan].checked ? (
                      <Checkmark />
                    ) : details[plan].text ? (
                      <span>{details[plan].text}</span>
                    ) : (
                      <span className='text-[#BBBBBB]'>—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};
